// src/pages/client-tabs/ClientQuotesTab.jsx
import React, { useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiSearch,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiArrowUp,
  FiArrowDown,
  FiMoreHorizontal,
  FiFileText,
  FiEdit2,
  FiTrash2,
  FiRefreshCcw,
  FiCalendar,
} from 'react-icons/fi';

import api from '../../api/axios';
import EditQuoteModal from '../../components/EditQuoteModal';
import {
  listQuotes,
  acceptQuote as svcAcceptQuote,
  declineQuote as svcDeclineQuote,
  cancelQuote as svcCancelQuote,
  convertToInvoice as svcConvertToInvoice,
  deleteQuote as svcDeleteQuote,
} from '../../services/quoteService';

/* ───────────────────────── helpers ───────────────────────── */

const fmtMoney = (n, c = 'USD') =>
  (typeof n === 'number' ? n : Number(n || 0)).toLocaleString(undefined, {
    style: 'currency',
    currency: c || 'USD',
  });

// lock to UTC date-only to avoid off-by-one rendering
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      })
    : '—';

const resolveId = (row) => row?.quoteId || row?._id || row?.id;

const LIFECYCLE_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Sent', value: 'sent' },
  { label: 'Accepted', value: 'accepted' },
  { label: 'Declined', value: 'declined' },
  { label: 'Expired', value: 'expired' },
  { label: 'Canceled', value: 'canceled' },
];

const LIFECYCLE_CHIP = {
  draft: 'bg-slate-100 text-slate-800 border border-slate-200',
  sent: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
  accepted: 'bg-green-100 text-green-700 border border-green-200',
  declined: 'bg-rose-100 text-rose-700 border border-rose-200',
  expired: 'bg-amber-100 text-amber-700 border border-amber-200',
  canceled: 'bg-zinc-100 text-zinc-800 border border-zinc-200',
};

const SORTABLE = [
  { id: 'createdAt', label: 'Created' },
  { id: 'updatedAt', label: 'Updated' },
  { id: 'issueDate', label: 'Issue Date' },
  { id: 'expiryDate', label: 'Expiry Date' },
  { id: 'quoteNumber', label: 'Quote #' },
  { id: 'totalAmount', label: 'Total' },
];

/* ───────────────────────── Action menu (portal) ───────────────────────── */

function ActionMenuPortal({
  anchorEl,
  row,
  canMutate,
  onClose,
  onView,
  onOpenPdf,
  onQuickEdit,
  onAccept,
  onDecline,
  onCancel,
  onConvert,
  onDelete,
}) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, origin: 'top-right' });

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const vh = window.innerHeight;
    const menuW = 240;
    const gap = 8;

    let left = r.right - menuW;
    if (left < 8) left = Math.max(8, r.left);

    let top = r.bottom + gap;
    let origin = 'top-right';
    setStyle((s) => ({ ...s, top, left, origin }));

    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const h = el.offsetHeight;
      if (top + h > vh - 8) {
        top = Math.max(8, r.top - h - gap);
        origin = 'bottom-right';
      }
      setStyle({ top, left, origin });
    });
  }, [anchorEl]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => e.key === 'Escape' && onClose();
    const onScrollOrResize = () => onClose();

    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [anchorEl, onClose]);

  const id = resolveId(row);
  const status = String(row?.lifecycleStatus || '').toLowerCase();
  const isExpired =
    status === 'expired' || (row?.expiryDate && new Date(row.expiryDate) < new Date());
  const locked = ['accepted', 'canceled'].includes(status) || isExpired;
  const convertDisabled = !canMutate || ['declined', 'canceled', 'expired'].includes(status);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{ position: 'fixed', top: style.top, left: style.left, zIndex: 50 }}
      className={`min-w-[240px] rounded-xl border border-slate-200 bg-white shadow-xl origin-${style.origin} transform transition duration-100`}
    >
      <button role="menuitem" className="w-full px-3 py-2 text-left hover:bg-slate-50" onClick={() => { onClose(); onView(id); }}>
        View details
      </button>

      <button role="menuitem" className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2" onClick={async () => { onClose(); await onOpenPdf(id); }}>
        <FiFileText /> Open PDF
      </button>

      <button role="menuitem" className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2" onClick={() => { onClose(); locked ? onView(id) : onQuickEdit(id); }}>
        <FiEdit2 /> {locked ? 'View' : 'Edit'}
      </button>

      <div className="my-1 h-px bg-slate-100" />

      <button
        role="menuitem"
        disabled={!canMutate || locked}
        className={`w-full px-3 py-2 text-left ${!canMutate || locked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'}`}
        onClick={() => { onClose(); onAccept(row); }}
      >
        Accept
      </button>

      <button
        role="menuitem"
        disabled={!canMutate || locked}
        className={`w-full px-3 py-2 text-left ${!canMutate || locked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'}`}
        onClick={() => { onClose(); onDecline(row); }}
      >
        Decline
      </button>

      <button
        role="menuitem"
        disabled={!canMutate}
        className={`w-full px-3 py-2 text-left ${!canMutate ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'}`}
        onClick={() => { onClose(); onCancel(row); }}
      >
        Cancel
      </button>

      <button
        role="menuitem"
        disabled={convertDisabled}
        className={`w-full px-3 py-2 text-left ${convertDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'}`}
        onClick={() => { onClose(); onConvert(row); }}
      >
        Convert → Invoice
      </button>

      <button
        role="menuitem"
        disabled={!canMutate}
        className={`w-full px-3 py-2 text-left text-rose-600 ${!canMutate ? 'opacity-40 cursor-not-allowed' : 'hover:bg-rose-50'}`}
        onClick={() => { onClose(); onDelete(row); }}
      >
        <FiTrash2 className="inline -mt-0.5 mr-1" /> Archive
      </button>
    </div>,
    document.body
  );
}

/* ───────────────────────── main ───────────────────────── */

export default function ClientQuotesTab({ clientId, canEdit }) {
  const navigate = useNavigate();
  const searchRef = useRef(null);

  // Query state (scoped to this client)
  const [search, setSearch] = useState('');
  const [lifecycleStatus, setLifecycleStatus] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25); // fixed page size (UI hidden)

  // Data
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 25 });
  const [loading, setLoading] = useState(true);

  // UI state
  const [editingId, setEditingId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  // Reset
  const resetFilters = () => {
    setSearch('');
    setLifecycleStatus('all');
    setSortBy('createdAt');
    setSortDir('desc');
    setLimit(25);
    setPage(1);
  };

  /* ───── Fetch ───── */

  const fetchQuotes = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const statusParam = lifecycleStatus === 'all' ? undefined : lifecycleStatus;

      const params = {
        clientId,
        page,
        limit,
        search: search.trim() || undefined,
        lifecycleStatus: statusParam,
        status: statusParam, // compatibility with older API
        sortBy,
        sortOrder: sortDir,
      };

      const resp = await listQuotes(params);
      const items = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.items)
        ? resp.items
        : [];

      setRows(items);
      const m = resp?.meta || {};
      setMeta({
        page: Number(m.page || 1),
        limit: Number(m.limit || limit),
        total: Number(m.total || items.length || 0),
        totalPages:
          Number(m.totalPages) ||
          (m.total ? Math.ceil(Number(m.total) / Number(m.limit || limit || 25)) : 1),
      });
    } catch (e) {
      console.error(e);
      toast.error('Failed to load quotes');
      setRows([]);
      setMeta({ page: 1, totalPages: 1, total: 0, limit });
    } finally {
      setLoading(false);
    }
  }, [clientId, page, limit, search, lifecycleStatus, sortBy, sortDir]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  /* ───── Actions ───── */

  const openPdf = useCallback(
    async (id) => {
      try {
        const row = rows.find((r) => resolveId(r) === id) || {};
        const fileName = `${row.quoteNumber || 'quote'}.pdf`.replace(/[^\w.-]+/g, '_');

        const resp = await api.get(`/quotes/${id}/pdf`, {
          responseType: 'blob',
          params: {
            download: 1,
            v: row?.updatedAt ? Number(new Date(row.updatedAt)) : undefined,
          },
        });

        const blob = resp?.data;
        const ct = String(resp?.headers?.['content-type'] || '').toLowerCase();

        if (ct.includes('application/json')) {
          const text = await blob.text?.();
          try {
            const json = JSON.parse(text || '{}');
            throw new Error(json?.error || 'Server error while generating PDF');
          } catch {
            throw new Error(text || 'Server error while generating PDF');
          }
        }

        if (!(blob instanceof Blob) || blob.size === 0) {
          throw new Error('Empty PDF response');
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error(e);
        toast.error(e?.message || 'PDF download failed');
      }
    },
    [rows]
  );

  const isExpiredRow = (row) => {
    const status = String(row?.lifecycleStatus || '').toLowerCase();
    if (status === 'expired') return true;
    if (row?.expiryDate) return new Date(row.expiryDate) < new Date();
    return false;
  };

  const acceptQuote = async (row) => {
    if (!canEdit) return;
    if (isExpiredRow(row)) return toast.error('Quote is expired; cannot accept.');
    if (!window.confirm(`Mark quote ${row.quoteNumber} as accepted?`)) return;
    try {
      await svcAcceptQuote(resolveId(row), {});
      toast.success('Quote accepted');
      fetchQuotes();
    } catch {
      toast.error('Failed to accept quote');
    }
  };

  const declineQuote = async (row) => {
    if (!canEdit) return;
    if (isExpiredRow(row)) return toast.error('Quote is expired; cannot decline.');
    if (!window.confirm(`Mark quote ${row.quoteNumber} as declined?`)) return;
    try {
      await svcDeclineQuote(resolveId(row), {});
      toast.success('Quote declined');
      fetchQuotes();
    } catch {
      toast.error('Failed to decline quote');
    }
  };

  const cancelQuote = async (row) => {
    if (!canEdit) return;
    if (!window.confirm(`Cancel this quote?`)) return;
    try {
      await svcCancelQuote(resolveId(row), {});
      toast.success('Quote canceled');
      fetchQuotes();
    } catch {
      toast.error('Failed to cancel quote');
    }
  };

  const convertQuote = async (row) => {
    if (!canEdit) return;
    const status = String(row?.lifecycleStatus || '').toLowerCase();
    if (['declined', 'canceled', 'expired'].includes(status)) {
      toast.error('This quote cannot be converted.');
      return;
    }
    if (!window.confirm(`Convert quote ${row.quoteNumber} to an invoice?`)) return;
    try {
      const resp = await svcConvertToInvoice(resolveId(row), {});
      const newId =
        resp?.data?.invoiceId ||
        resp?.invoiceId ||
        resp?.data?.id ||
        resp?.id;
      toast.success('Converted to invoice');
      if (newId) navigate(`/invoices/${newId}`);
      else fetchQuotes();
    } catch {
      toast.error('Failed to convert quote');
    }
  };

  const deleteQuote = async (row) => {
    if (!canEdit) return;
    if (!window.confirm('Archive this quote?')) return;
    try {
      await svcDeleteQuote(resolveId(row));
      toast.success('Quote archived');
      fetchQuotes();
    } catch {
      toast.error('Failed to archive quote');
    }
  };

  /* ───── Derived ───── */

  const isLocked = (row) => {
    const status = String(row?.lifecycleStatus || '').toLowerCase();
    return ['accepted', 'canceled'].includes(status) || isExpiredRow(row);
  };

  const SortHeader = ({ id, label }) => {
    const active = sortBy === id;
    return (
      <button
        type="button"
        onClick={() => {
          if (sortBy === id) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          else {
            setSortBy(id);
            setSortDir('asc');
          }
          setPage(1);
        }}
        className={`inline-flex items-center gap-1 ${
          active ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        {label}
        {active ? (sortDir === 'asc' ? <FiArrowUp /> : <FiArrowDown />) : null}
      </button>
    );
  };

  /* ───── UI ───── */

  return (
    <div className="w-full">
      {/* Filters (Invoices-style: horizontal scroll when tight) */}
      <div className="mb-4">
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-3 overflow-x-auto">
          <div className="flex gap-2 items-center min-w-max">
            {/* Search */}
            <div className="relative w-64">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search quote #, notes…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-xl bg-white focus:outline-none focus:ring-4 focus:ring-blue-100"
              />
            </div>

            {/* Lifecycle */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={lifecycleStatus}
                onChange={(e) => {
                  setLifecycleStatus(e.target.value);
                  setPage(1);
                }}
                title="Lifecycle"
              >
                {LIFECYCLE_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Sort / dir */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
                title="Sort by"
              >
                {SORTABLE.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={sortDir}
                onChange={(e) => {
                  setSortDir(e.target.value);
                  setPage(1);
                }}
                title="Sort direction"
              >
                <option value="desc">Newest</option>
                <option value="asc">Oldest</option>
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Reset */}
            <button
              className="flex items-center gap-2 px-3 py-2 text-sm border rounded-xl bg-white hover:bg-slate-50"
              onClick={resetFilters}
              title="Reset filters"
            >
              <FiRefreshCcw /> Reset
            </button>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="w-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="quoteNumber" label="Quote #" />
                </th>
                {/* Client column removed for client-scoped tab */}
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="issueDate" label="Issue" />
                </th>
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="expiryDate" label="Expiry" />
                </th>
                <th className="px-4 py-3 text-left font-bold">Lifecycle</th>
                <th className="px-4 py-3 text-right font-bold">
                  <SortHeader id="totalAmount" label="Total" />
                </th>
                <th className="px-4 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-4 py-4">
                      <div className="h-4 w-full rounded bg-slate-100 animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-14">
                    <div className="text-center text-slate-500">No quotes for this client.</div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const id = resolveId(row);
                  const locked = isLocked(row);
                  const status = String(row?.lifecycleStatus || '').toLowerCase();
                  const convertDisabled = ['declined', 'canceled', 'expired'].includes(status);

                  return (
                    <tr key={id} className="group transition hover:bg-blue-50/40">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          className="font-semibold text-blue-700 hover:underline"
                          onClick={() => navigate(`/quotes/${id}`)}
                          title="View details"
                        >
                          {row.quoteNumber}
                        </button>
                        <div className="text-xs text-slate-500">{row.currency || 'USD'}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDate(row.issueDate)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDate(row.expiryDate)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            LIFECYCLE_CHIP[row.lifecycleStatus] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.lifecycleStatus?.charAt(0).toUpperCase() +
                            row.lifecycleStatus?.slice(1) || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {fmtMoney(row.totalAmount, row.currency)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex justify-end">
                          <button
                            className="p-2 rounded hover:bg-slate-100"
                            onClick={(e) => {
                              setMenuRow(row);
                              setMenuAnchor(e.currentTarget);
                            }}
                            aria-haspopup="menu"
                            aria-expanded={menuRow && resolveId(menuRow) === id ? 'true' : 'false'}
                            title="Actions"
                          >
                            <FiMoreHorizontal />
                          </button>
                        </div>

                        {/* Quick inline actions hook placeholder */}
                        <div className="hidden" data-convert-disabled={convertDisabled} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile list */}
        <div className="md:hidden divide-y divide-slate-100">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-slate-500">No quotes for this client.</div>
          ) : (
            rows.map((row) => {
              const id = resolveId(row);
              const locked = isLocked(row);
              const status = String(row?.lifecycleStatus || '').toLowerCase();
              const convertDisabled = ['declined', 'canceled', 'expired'].includes(status);

              return (
                <div key={id} className="p-4 hover:bg-blue-50/60 transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900">
                        <button
                          className="hover:underline"
                          onClick={() => navigate(`/quotes/${id}`)}
                          title="View details"
                        >
                          {row.quoteNumber}
                        </button>
                      </div>
                      <div className="text-sm text-slate-600 flex items-center gap-2">
                        <span className="inline-flex items-center gap-1">
                          <FiCalendar /> Issue {fmtDate(row.issueDate)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <FiCalendar /> Expiry {fmtDate(row.expiryDate)}
                        </span>
                      </div>
                      <div className="mt-1">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            LIFECYCLE_CHIP[row.lifecycleStatus] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.lifecycleStatus?.charAt(0).toUpperCase() +
                            row.lifecycleStatus?.slice(1) || '—'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{fmtMoney(row.totalAmount, row.currency)}</div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button className="px-3 py-1.5 rounded-xl border text-sm" onClick={() => navigate(`/quotes/${id}`)}>
                      View
                    </button>

                    <button className="px-3 py-1.5 rounded-xl border text-sm" onClick={async () => openPdf(id)}>
                      <FiFileText className="inline -mt-0.5 mr-1" />
                      PDF
                    </button>

                    <button
                      className="px-3 py-1.5 rounded-xl border text-sm"
                      onClick={() => {
                        if (locked) navigate(`/quotes/${id}`);
                        else setEditingId(id);
                      }}
                    >
                      <FiEdit2 className="inline -mt-0.5 mr-1" />
                      {locked ? 'View' : 'Edit'}
                    </button>

                    {canEdit && (
                      <>
                        <button
                          className={`px-3 py-1.5 rounded-xl border text-sm ${locked ? 'opacity-40 cursor-not-allowed' : ''}`}
                          onClick={() => acceptQuote(row)}
                          disabled={locked}
                        >
                          Accept
                        </button>
                        <button
                          className={`px-3 py-1.5 rounded-xl border text-sm ${locked ? 'opacity-40 cursor-not-allowed' : ''}`}
                          onClick={() => declineQuote(row)}
                          disabled={locked}
                        >
                          Decline
                        </button>
                        <button className="px-3 py-1.5 rounded-xl border text-sm" onClick={() => cancelQuote(row)}>
                          Cancel
                        </button>
                        <button
                          className={`px-3 py-1.5 rounded-xl border text-sm ${convertDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                          onClick={() => convertQuote(row)}
                          disabled={convertDisabled}
                        >
                          Convert
                        </button>
                        <button
                          className="px-3 py-1.5 rounded-xl border text-sm text-rose-600 border-rose-200"
                          onClick={() => deleteQuote(row)}
                        >
                          <FiTrash2 className="inline -mt-0.5 mr-1" />
                          Archive
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-sm text-slate-600">
            Page <span className="font-semibold">{meta.page}</span> of{' '}
            <span className="font-semibold">{meta.totalPages}</span> •{' '}
            <span className="font-semibold">{meta.total}</span> total
          </div>
          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={meta.page <= 1 || loading}
              title="Previous"
            >
              <FiChevronLeft />
            </button>
            <span className="text-sm text-slate-700">
              Page <span className="font-semibold">{meta.page}</span> of{' '}
              <span className="font-semibold">{meta.totalPages}</span>
            </span>
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.min(meta.totalPages || 1, p + 1))}
              disabled={meta.page >= (meta.totalPages || 1) || loading}
              title="Next"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Portaled action menu */}
      {menuAnchor && menuRow && (
        <ActionMenuPortal
          anchorEl={menuAnchor}
          row={menuRow}
          canMutate={!!canEdit}
          onClose={() => {
            setMenuAnchor(null);
            setMenuRow(null);
          }}
          onView={(id) => navigate(`/quotes/${id}`)}
          onOpenPdf={openPdf}
          onQuickEdit={(id) => setEditingId(id)}
          onAccept={acceptQuote}
          onDecline={declineQuote}
          onCancel={cancelQuote}
          onConvert={convertQuote}
          onDelete={deleteQuote}
        />
      )}

      {/* Edit modal */}
      {editingId && (
        <EditQuoteModal
          key={editingId}
          isOpen={!!editingId}
          quoteId={editingId}
          onClose={() => setEditingId(null)}
          onUpdated={() => {
            setEditingId(null);
            fetchQuotes();
            toast.success('Quote updated');
          }}
        />
      )}
    </div>
  );
}
